import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { 
  Search, Plus, Edit2, Trash2, Loader2, Check, X, Code, 
  Clock, CheckCircle2, ArrowRight, ExternalLink, Calendar, MapPin, Truck, Package
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { 
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, 
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle 
} from '../../components/ui/alert-dialog';

import Pagination from '../../components/ui/pagination';

const BookingRequestList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const getAuthHeaders = useCallback(() => ({
    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
    'Content-Type': 'application/json'
  }), []);

  const [bookings, setBookings] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Active status tab: 'PENDING' (default), 'CONFIRMED', or 'all'
  const activeStatus = searchParams.get('status') || 'PENDING';
  const [statusFilter, setStatusFilter] = useState(activeStatus);

  // Tab counts
  const [pendingCount, setPendingCount] = useState(0);
  const [completedCount, setCompletedCount] = useState(0);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showEmbed, setShowEmbed] = useState(false);

  // Detect role & own company
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [ownCompanyId, setOwnCompanyId] = useState('');
  const [companies, setCompanies] = useState([]);
  const [embedCompanyId, setEmbedCompanyId] = useState('');

  useEffect(() => {
    try {
      const token = localStorage.getItem('auth_token');
      const payload = JSON.parse(atob(token.split('.')[1]));
      const isSuper = payload.role === 'super_admin';
      setIsSuperAdmin(isSuper);
      const cid = payload.company_id || '';
      setOwnCompanyId(cid);
      setEmbedCompanyId(cid);
    } catch {}
  }, []);

  // Fetch company list for super admin embed selector
  useEffect(() => {
    if (!isSuperAdmin) return;
    fetch(`${backendUrl}/api/companies`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        setCompanies(Array.isArray(data) ? data : []);
        if (data.length > 0) setEmbedCompanyId(data[0].id);
      })
      .catch(() => {});
  }, [isSuperAdmin, backendUrl, getAuthHeaders]);

  // Sync statusFilter from URL searchParams
  useEffect(() => {
    const urlStatus = searchParams.get('status') || 'PENDING';
    setStatusFilter(urlStatus);
    setPage(1);
  }, [searchParams]);

  // Fetch counts for tabs
  const fetchCounts = useCallback(async () => {
    try {
      const pRes = await fetch(`${backendUrl}/api/booking-requests?status=PENDING&limit=1`, { headers: getAuthHeaders() });
      if (pRes.ok) {
        const pData = await pRes.json();
        setPendingCount(pData.total || 0);
      }
      const cRes = await fetch(`${backendUrl}/api/booking-requests?status=CONFIRMED&limit=1`, { headers: getAuthHeaders() });
      if (cRes.ok) {
        const cData = await cRes.json();
        setCompletedCount(cData.total || 0);
      }
    } catch {}
  }, [backendUrl, getAuthHeaders]);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  const embedCode = `<iframe src="${backendUrl}/booking/embed?company_id=${embedCompanyId}" width="100%" height="700" frameborder="0" style="border:none;border-radius:12px;box-shadow:0 2px 12px rgba(0,0,0,0.08);"></iframe>`;

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      params.append('skip', String((page - 1) * pageSize));
      params.append('limit', String(pageSize));
      const res = await fetch(`${backendUrl}/api/booking-requests?${params}`, { headers: getAuthHeaders() });
      if (res.ok) {
        const d = await res.json();
        setBookings(d.items || []);
        setTotal(d.total || 0);
      }
    } catch {
      toast({ title: "Error", description: "Failed to load bookings", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [backendUrl, getAuthHeaders, search, statusFilter, page, pageSize, toast]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const handleTabChange = (status) => {
    setStatusFilter(status);
    setSearchParams(status === 'PENDING' ? { status: 'PENDING' } : { status });
    setPage(1);
  };

  const handleConfirm = async (b) => {
    try {
      const res = await fetch(`${backendUrl}/api/booking-requests/${b.id}/confirm`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (!res.ok) throw new Error();
      const resData = await res.json();
      const docketMsg = resData.created_docket_no ? ` | Docket ${resData.created_docket_no} Created` : '';
      toast({
        title: "Booking Confirmed",
        description: `${b.inquiry_number} confirmed${docketMsg}`
      });
      fetchCounts();
      fetchBookings();

      if (b.category === 'FREIGHT_FORWARDING') {
        // Freight Forwarding: ONLY Docket is created! Navigate to Dockets
        navigate('/dockets');
      } else {
        // Domestic Logistics: Docket is created AND fleet trip can be assigned
        const tripType = (b.trip || 'local').toLowerCase();
        const tripKind = (b.trip_type || 'single').toLowerCase() === 'round' ? 'round_trip' : 'single_trip';
        const docketParam = resData.created_docket_id ? `&docket_id=${resData.created_docket_id}` : '';
        navigate(`/fleet/trips/add?from_booking=${b.id}&from=${encodeURIComponent(b.from_location_name || '')}&to=${encodeURIComponent(b.to_location_name || '')}&trip_type=${tripType}&trip_kind=${tripKind}${docketParam}`);
      }
    } catch {
      toast({ title: "Error", description: "Failed to confirm booking request", variant: "destructive" });
    }
  };

  const handleReject = async (b) => {
    try {
      const res = await fetch(`${backendUrl}/api/booking-requests/${b.id}/reject`, { method: 'POST', headers: getAuthHeaders() });
      if (!res.ok) throw new Error();
      toast({ title: "Rejected", description: `${b.inquiry_number} rejected` });
      fetchCounts();
      fetchBookings();
    } catch {
      toast({ title: "Error", description: "Failed to reject", variant: "destructive" });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const res = await fetch(`${backendUrl}/api/booking-requests/${deleteTarget.id}`, { method: 'DELETE', headers: getAuthHeaders() });
      if (!res.ok) throw new Error();
      toast({ title: "Deleted", description: "Booking request removed" });
      fetchCounts();
      fetchBookings();
    } catch {
      toast({ title: "Error", description: "Failed to delete", variant: "destructive" });
    } finally {
      setDeleteTarget(null);
    }
  };

  const statusBadge = (s) => {
    const map = {
      PENDING: 'bg-amber-100 text-amber-800 border border-amber-300',
      CONFIRMED: 'bg-emerald-100 text-emerald-800 border border-emerald-300',
      REJECTED: 'bg-red-100 text-red-700 border border-red-300',
    };
    return map[s] || 'bg-gray-100 text-gray-700';
  };

  const cols = [
    '#',
    'Inquiry #',
    'Date',
    'Customer / Contact',
    'Category & Service',
    'Route (Origin ➔ Destination)',
    'Vehicle / Fleet',
    'Load & Qty',
    'Status',
    'Actions'
  ];

  return (
    <div className="space-y-6">
      {/* ─── Page Title & Action Bar ─── */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 data-testid="booking-list-title" className="text-2xl sm:text-3xl font-bold text-text-dark font-manrope">
            Bookings
          </h1>
          <p className="text-xs sm:text-sm text-text-secondary mt-1">
            Manage incoming consignments, pending customer booking requests, and completed shipments.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Button
            data-testid="embed-code-btn"
            onClick={() => setShowEmbed(!showEmbed)}
            variant="outline"
            className="border-accent-blue text-accent-blue hover:bg-accent-blue/5 font-ibm font-semibold h-10 px-4 rounded-lg text-xs"
          >
            <Code className="w-4 h-4 mr-2" />
            Get Embed Code
          </Button>

          <Button
            data-testid="new-inquiry-btn"
            onClick={() => navigate('/booking/add')}
            className="bg-accent-blue hover:bg-accent-blue/90 text-white font-ibm font-semibold h-10 px-5 rounded-lg text-xs shadow-sm flex items-center gap-1.5"
          >
            <Plus className="w-4 h-4" />
            <span>+ New Booking</span>
          </Button>
        </div>
      </div>

      {/* ─── Embed Code Box (Superadmin or Share) ─── */}
      {showEmbed && (
        <Card className="bg-blue-50 border border-blue-200 rounded-xl p-5 animate-fadeIn">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-bold text-blue-900 font-ibm">Embeddable Booking Form</h3>
            <button onClick={() => setShowEmbed(false)} className="text-blue-400 hover:text-blue-600">
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="text-xs text-blue-700 font-ibm mb-3">
            Copy this HTML code to display the public shipment booking form directly on your company website or customer landing page.
          </p>

          {isSuperAdmin && companies.length > 0 && (
            <div className="mb-3">
              <label className="text-xs font-semibold text-blue-900 font-ibm block mb-1">Company Association</label>
              <select
                data-testid="embed-company-select"
                value={embedCompanyId}
                onChange={e => setEmbedCompanyId(e.target.value)}
                className="w-full max-w-xs px-3 py-1.5 text-xs border border-blue-200 rounded-lg bg-white font-ibm focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                {companies.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          )}

          <div className="relative">
            <textarea
              readOnly
              value={embedCode}
              className="w-full h-20 font-mono text-xs p-3 bg-white border border-blue-200 rounded-lg resize-none focus:outline-none"
            />
            <Button
              size="sm"
              onClick={() => {
                navigator.clipboard.writeText(embedCode);
                toast({ title: "Copied!", description: "Embed code copied to clipboard" });
              }}
              className="absolute top-2 right-2 bg-accent-blue hover:bg-accent-blue/90 text-white text-xs h-7 px-3 font-ibm"
            >
              Copy
            </Button>
          </div>
        </Card>
      )}

      {/* ─── Navigation Tabs: Pending vs Completed vs All ─── */}
      <div className="flex items-center border-b border-gray-200 gap-2">
        <button
          onClick={() => handleTabChange('PENDING')}
          className={`pb-3 px-4 font-ibm font-bold text-sm flex items-center gap-2 border-b-2 transition-all cursor-pointer ${
            statusFilter === 'PENDING'
              ? 'border-accent-blue text-accent-blue'
              : 'border-transparent text-text-secondary hover:text-text-dark hover:border-gray-300'
          }`}
        >
          <Clock className="w-4 h-4 text-amber-500" />
          <span>Pending Bookings</span>
          <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
            statusFilter === 'PENDING' ? 'bg-amber-100 text-amber-900' : 'bg-gray-100 text-gray-600'
          }`}>
            {pendingCount}
          </span>
        </button>

        <button
          onClick={() => handleTabChange('CONFIRMED')}
          className={`pb-3 px-4 font-ibm font-bold text-sm flex items-center gap-2 border-b-2 transition-all cursor-pointer ${
            statusFilter === 'CONFIRMED'
              ? 'border-emerald-600 text-emerald-700'
              : 'border-transparent text-text-secondary hover:text-text-dark hover:border-gray-300'
          }`}
        >
          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
          <span>Completed Bookings</span>
          <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
            statusFilter === 'CONFIRMED' ? 'bg-emerald-100 text-emerald-900' : 'bg-gray-100 text-gray-600'
          }`}>
            {completedCount}
          </span>
        </button>

        <button
          onClick={() => handleTabChange('all')}
          className={`pb-3 px-4 font-ibm font-bold text-sm flex items-center gap-2 border-b-2 transition-all cursor-pointer ${
            statusFilter === 'all'
              ? 'border-slate-800 text-slate-900'
              : 'border-transparent text-text-secondary hover:text-text-dark hover:border-gray-300'
          }`}
        >
          <span>All Bookings</span>
        </button>
      </div>

      {/* ─── Search & Table Card ─── */}
      <Card className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-sm text-text-secondary font-ibm">
            <span>Showing</span>
            <strong className="text-text-dark">{bookings.length}</strong>
            <span>of</span>
            <strong className="text-text-dark">{total}</strong>
            <span className="capitalize">{statusFilter === 'all' ? 'total' : statusFilter.toLowerCase()} bookings</span>
          </div>

          <div className="flex items-center gap-3">
            <div className="relative w-64 sm:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary" />
              <Input
                data-testid="booking-search"
                placeholder="Search by inquiry #, customer, city..."
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
                className="pl-9 h-9 text-xs font-ibm rounded-lg"
              />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 text-text-secondary">
            <Loader2 className="w-8 h-8 animate-spin text-accent-blue mb-2" />
            <span className="text-xs font-ibm">Loading booking requests...</span>
          </div>
        ) : bookings.length === 0 ? (
          <div className="text-center py-16 text-text-secondary font-ibm space-y-3">
            <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto text-slate-400">
              <Package className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm font-semibold text-text-dark">
                {statusFilter === 'PENDING'
                  ? 'No pending booking requests'
                  : statusFilter === 'CONFIRMED'
                  ? 'No completed bookings recorded'
                  : 'No bookings found matching criteria'}
              </p>
              <p className="text-xs text-slate-500 mt-0.5">
                {statusFilter === 'PENDING'
                  ? 'New online inquiries from your portal or website will appear here for review.'
                  : 'Confirmed and dispatched bookings will be logged here.'}
              </p>
            </div>
            {statusFilter === 'PENDING' && (
              <Button
                onClick={() => navigate('/booking/add')}
                className="mt-2 bg-accent-blue text-white font-ibm text-xs h-9 px-4 rounded-lg"
              >
                <Plus className="w-4 h-4 mr-1.5" /> Create New Booking
              </Button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left" data-testid="bookings-table">
              <thead className="bg-gray-50/80 border-b border-gray-200">
                <tr>
                  {cols.map((h) => (
                    <th
                      key={h}
                      className="px-3.5 py-3 text-xs font-bold text-text-secondary uppercase tracking-wider font-ibm whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-xs font-ibm">
                {bookings.map((b, idx) => (
                  <tr key={b.id} className="hover:bg-slate-50/70 transition-colors">
                    {/* Index */}
                    <td className="px-3.5 py-3 text-text-secondary">
                      {(page - 1) * pageSize + idx + 1}
                    </td>

                    {/* Inquiry # */}
                    <td className="px-3.5 py-3 font-semibold text-text-dark whitespace-nowrap">
                      <span className="bg-blue-50 text-accent-blue px-2 py-1 rounded font-mono font-bold">
                        {b.inquiry_number || '—'}
                      </span>
                    </td>

                    {/* Date */}
                    <td className="px-3.5 py-3 whitespace-nowrap text-slate-600">
                      <div className="font-medium text-slate-800">{b.booking_date || b.inquiry_date}</div>
                      {b.inquiry_date && b.booking_date && b.booking_date !== b.inquiry_date && (
                        <div className="text-[11px] text-slate-400">Inq: {b.inquiry_date}</div>
                      )}
                    </td>

                    {/* Customer & Contact */}
                    <td className="px-3.5 py-3">
                      <div className="font-bold text-text-dark">
                        {b.customer_name || b.contact_name || '—'}
                      </div>
                      <div className="text-[11px] text-slate-500">
                        {b.contact_name && b.contact_name !== b.customer_name ? `${b.contact_name} • ` : ''}
                        {b.contact_phone || ''}
                      </div>
                    </td>

                    {/* Category & Service */}
                    <td className="px-3.5 py-3">
                      <div className="font-semibold text-slate-800">
                        {b.service_type || b.vehicle_type_name || 'Standard'}
                      </div>
                      <div className="text-[11px] text-slate-500">
                        {b.category === 'FREIGHT_FORWARDING' ? 'Freight Forwarding' : 'Domestic Logistics'}
                      </div>
                    </td>

                    {/* Route */}
                    <td className="px-3.5 py-3 max-w-[200px]">
                      <div className="font-medium text-slate-800 truncate" title={`${b.from_location_name} ➔ ${b.to_location_name}`}>
                        {b.from_location_name || '—'} ➔ {b.to_location_name || '—'}
                      </div>
                    </td>

                    {/* Vehicle / Fleet */}
                    <td className="px-3.5 py-3 whitespace-nowrap">
                      <div>
                        {b.category === 'FREIGHT_FORWARDING'
                          ? (b.extra_details?.mode_of_transport ? `${b.extra_details.mode_of_transport} Freight` : 'Freight Forwarding')
                          : (b.vehicle_type_name || 'Any Fleet')}
                      </div>
                      <div className="text-[11px] text-accent-blue font-medium">
                        {b.category === 'FREIGHT_FORWARDING'
                          ? (b.extra_details?.carrier_name || (b.extra_details?.sea_shipment_type ? `Ocean ${b.extra_details.sea_shipment_type}` : ''))
                          : (b.vehicle_size || '')}
                      </div>
                    </td>

                    {/* Load & Qty */}
                    <td className="px-3.5 py-3 whitespace-nowrap">
                      <div>
                        {b.gross_weight || b.weight ? `${b.gross_weight || b.weight} Kg` : ''}
                        {b.packages ? ` • ${b.packages} pkgs` : ''}
                      </div>
                      <div className="text-[11px] text-slate-500">
                        {b.extra_details?.total_cbm ? `${b.extra_details.total_cbm} CBM • ` : ''}
                        {b.load_type || b.extra_details?.cargo_type || ''}
                      </div>
                    </td>

                    {/* Status */}
                    <td className="px-3.5 py-3 whitespace-nowrap">
                      <Badge className={`text-[11px] font-bold ${statusBadge(b.status)}`}>
                        {b.status}
                      </Badge>
                    </td>

                    {/* Actions */}
                    <td className="px-3.5 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        {b.status === 'PENDING' ? (
                          <>
                            <button
                              onClick={() => handleConfirm(b)}
                              title="Confirm & Dispatch to Fleet"
                              className="w-7 h-7 flex items-center justify-center bg-emerald-600 hover:bg-emerald-700 text-white rounded-md transition-colors cursor-pointer"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleReject(b)}
                              title="Reject Request"
                              className="w-7 h-7 flex items-center justify-center bg-red-500 hover:bg-red-600 text-white rounded-md transition-colors cursor-pointer"
                            >
                              <X className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => navigate(`/booking/edit/${b.id}`)}
                              title="Edit Booking"
                              className="w-7 h-7 flex items-center justify-center bg-accent-blue hover:bg-accent-blue/90 text-white rounded-md transition-colors cursor-pointer"
                            >
                              <Edit2 className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => setDeleteTarget(b)}
                              title="Delete"
                              className="w-7 h-7 flex items-center justify-center border border-red-200 hover:bg-red-50 text-red-600 rounded-md transition-colors cursor-pointer"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </>
                        ) : (
                          <>
                            {b.trip_id && (
                              <button
                                onClick={() => navigate(`/fleet/trips?search=${b.trip_id}`)}
                                title="View Linked Trip in Fleet"
                                className="w-7 h-7 flex items-center justify-center bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-md transition-colors cursor-pointer border border-emerald-200"
                              >
                                <ExternalLink className="w-3.5 h-3.5" />
                              </button>
                            )}
                            <button
                              onClick={() => navigate(`/booking/edit/${b.id}`)}
                              title="Edit Booking"
                              className="w-7 h-7 flex items-center justify-center bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md transition-colors cursor-pointer"
                            >
                              <Edit2 className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => setDeleteTarget(b)}
                              title="Delete Record"
                              className="w-7 h-7 flex items-center justify-center border border-red-200 hover:bg-red-50 text-red-600 rounded-md transition-colors cursor-pointer"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && bookings.length > 0 && (
          <div className="pt-2 border-t border-gray-100">
            <Pagination
              total={total}
              page={page}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={v => { setPageSize(v); setPage(1); }}
            />
          </div>
        )}
      </Card>

      {/* ─── Delete Confirmation Modal ─── */}
      <AlertDialog open={!!deleteTarget} onOpenChange={v => { if (!v) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Booking Request</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete booking inquiry "{deleteTarget?.inquiry_number}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default BookingRequestList;
